export interface AdminNavItem {
  label: string;
  to: string;
  icon: string;
  phase: number;
  badge?: number;
  badgeKey?: "pendingBookings" | "pendingReviews";
}

export interface AdminNavGroup {
  heading: string;
  blurb: string;
  items: AdminNavItem[];
}

export const adminNav: AdminNavGroup[] = [
  {
    heading: "Dashboard",
    blurb: "A live overview of everything happening across Rythugaru Agri Tourism.",
    items: [{ label: "Dashboard", to: "/admin", icon: "ri-dashboard-line", phase: 2 }],
  },
  {
    heading: "Operations",
    blurb: "Run the day to day — bookings, the live calendar, availability rules and guest check-ins.",
    items: [
      { label: "Bookings", to: "/admin/bookings", icon: "ri-calendar-check-line", phase: 1, badgeKey: "pendingBookings" },
      { label: "Calendar", to: "/admin/calendar", icon: "ri-calendar-2-line", phase: 3 },
      { label: "Availability", to: "/admin/availability", icon: "ri-time-line", phase: 3 },
      { label: "Check-ins", to: "/admin/check-ins", icon: "ri-login-circle-line", phase: 3 },
    ],
  },
  {
    heading: "Customers",
    blurb: "A full visitor CRM — profiles, groups, reviews, complaints and support tickets.",
    items: [
      { label: "Visitors", to: "/admin/visitors", icon: "ri-user-3-line", phase: 4 },
      { label: "Visitor Groups", to: "/admin/visitor-groups", icon: "ri-group-line", phase: 4 },
      { label: "Reviews", to: "/admin/reviews", icon: "ri-star-line", phase: 4, badgeKey: "pendingReviews" },
      { label: "Complaints", to: "/admin/complaints", icon: "ri-emotion-unhappy-line", phase: 6 },
      { label: "Support Tickets", to: "/admin/support-tickets", icon: "ri-customer-service-2-line", phase: 6, badge: 4 },
    ],
  },
  {
    heading: "Farm Network",
    blurb: "Onboard and manage the farm network — applications, verification, training, safety audits and settlements.",
    items: [
      { label: "Farmers", to: "/admin/farmers", icon: "ri-user-star-line", phase: 4 },
      { label: "Farms", to: "/admin/farms", icon: "ri-plant-line", phase: 4 },
      { label: "Farmer Applications", to: "/admin/farmer-applications", icon: "ri-file-user-line", phase: 4, badge: 8 },
      { label: "Verification", to: "/admin/verification", icon: "ri-shield-check-line", phase: 4 },
      { label: "Training", to: "/admin/training", icon: "ri-graduation-cap-line", phase: 4 },
      { label: "Safety Audits", to: "/admin/safety-audits", icon: "ri-shield-flash-line", phase: 4, badge: 2 },
      { label: "Farmer Settlements", to: "/admin/farmer-settlements", icon: "ri-hand-coin-line", phase: 4 },
    ],
  },
  {
    heading: "Experiences",
    blurb: "Design the catalog — experiences, packages, reusable activities and pricing rules.",
    items: [
      { label: "Experiences", to: "/admin/experiences", icon: "ri-compass-3-line", phase: 5 },
      { label: "Packages", to: "/admin/packages", icon: "ri-archive-2-line", phase: 5 },
      { label: "Activities", to: "/admin/activities", icon: "ri-run-line", phase: 5 },
      { label: "Pricing", to: "/admin/pricing", icon: "ri-price-tag-3-line", phase: 5 },
    ],
  },
  {
    heading: "Content",
    blurb: "Manage every public-facing page, story, media asset and search setting from one CMS.",
    items: [
      { label: "Website Pages", to: "/admin/website-pages", icon: "ri-pages-line", phase: 6 },
      { label: "Homepage", to: "/admin/homepage", icon: "ri-home-4-line", phase: 6 },
      { label: "Konaseema & Trip Planner", to: "/admin/konaseema-trip", icon: "ri-road-map-line", phase: 6 },
      { label: "Blog", to: "/admin/blog", icon: "ri-article-line", phase: 6 },
      { label: "Farmer Stories", to: "/admin/farmer-stories", icon: "ri-book-open-line", phase: 6 },
      { label: "Story Submissions", to: "/admin/story-submissions", icon: "ri-inbox-archive-line", phase: 6 },
      { label: "Media Library", to: "/admin/media-library", icon: "ri-image-2-line", phase: 6 },
      { label: "FAQs", to: "/admin/faqs", icon: "ri-question-line", phase: 6 },
      { label: "Testimonials", to: "/admin/testimonials", icon: "ri-chat-quote-line", phase: 6 },
      { label: "SEO", to: "/admin/seo", icon: "ri-seo-line", phase: 6 },
    ],
  },
  {
    heading: "Commerce",
    blurb: "Track money end to end — payments, refunds, coupons, revenue and settlements.",
    items: [
      { label: "Payments", to: "/admin/payments", icon: "ri-bank-card-line", phase: 5 },
      { label: "Refunds", to: "/admin/refunds", icon: "ri-refund-2-line", phase: 5 },
      { label: "Coupons", to: "/admin/coupons", icon: "ri-coupon-3-line", phase: 5 },
      { label: "Revenue", to: "/admin/revenue", icon: "ri-line-chart-line", phase: 5 },
      { label: "Settlements", to: "/admin/settlements", icon: "ri-exchange-dollar-line", phase: 5 },
    ],
  },
  {
    heading: "Marketing",
    blurb: "Reach past visitors — campaigns and messaging across email, SMS and WhatsApp.",
    items: [
      { label: "Campaigns", to: "/admin/campaigns", icon: "ri-megaphone-line", phase: 6 },
      { label: "Notifications", to: "/admin/notifications", icon: "ri-notification-3-line", phase: 6 },
      { label: "Email", to: "/admin/email", icon: "ri-mail-send-line", phase: 6 },
      { label: "SMS", to: "/admin/sms", icon: "ri-message-2-line", phase: 6 },
      { label: "WhatsApp", to: "/admin/whatsapp", icon: "ri-whatsapp-line", phase: 6 },
    ],
  },
  {
    heading: "Safety & Compliance",
    blurb: "Keep every visit safe and every farm compliant — incidents, documents and audits.",
    items: [
      { label: "Incidents", to: "/admin/incidents", icon: "ri-alert-line", phase: 6 },
      { label: "Safety", to: "/admin/safety", icon: "ri-shield-star-line", phase: 6 },
      { label: "Documents", to: "/admin/documents", icon: "ri-folder-shield-2-line", phase: 6 },
      { label: "Compliance", to: "/admin/compliance", icon: "ri-checkbox-multiple-line", phase: 6 },
    ],
  },
  {
    heading: "Reports",
    blurb: "Understand the business with analytics and deep-dive operational reports.",
    items: [
      { label: "Analytics", to: "/admin/analytics", icon: "ri-bar-chart-box-line", phase: 6 },
      { label: "Booking Reports", to: "/admin/booking-reports", icon: "ri-file-chart-line", phase: 6 },
      { label: "Revenue Reports", to: "/admin/revenue-reports", icon: "ri-money-rupee-circle-line", phase: 6 },
      { label: "Farm Performance", to: "/admin/farm-performance", icon: "ri-speed-up-line", phase: 6 },
      { label: "Customer Reports", to: "/admin/customer-reports", icon: "ri-user-search-line", phase: 6 },
      { label: "Marketing Reports", to: "/admin/marketing-reports", icon: "ri-pie-chart-2-line", phase: 6 },
    ],
  },
  {
    heading: "Administration",
    blurb: "Control the platform itself — admin accounts, roles, audit trails and integrations.",
    items: [
      { label: "Admin Users", to: "/admin/admin-users", icon: "ri-admin-line", phase: 6 },
      { label: "Roles & Permissions", to: "/admin/roles-permissions", icon: "ri-lock-password-line", phase: 6 },
      { label: "Audit Logs", to: "/admin/audit-logs", icon: "ri-history-line", phase: 6 },
      { label: "System Settings", to: "/admin/system-settings", icon: "ri-settings-3-line", phase: 6 },
      { label: "Integrations", to: "/admin/integrations", icon: "ri-plug-line", phase: 6 },
    ],
  },
];

export const adminNavFlat: AdminNavItem[] = adminNav.flatMap((group) => group.items);

export interface AdminNavMatch {
  group: AdminNavGroup;
  item: AdminNavItem;
}

export function findNavItem(pathname: string): AdminNavMatch | null {
  const clean = pathname.replace(/\/+$/, "") || "/admin";
  const item = adminNavFlat.find((entry) => entry.to === clean);
  if (!item) return null;
  const group = adminNav.find((entry) => entry.items.includes(item));
  if (!group) return null;
  return { group, item };
}

export interface AdminSearchHit {
  category: string;
  label: string;
  sub?: string;
  icon: string;
  to: string;
}

export const adminSearchIndex: AdminSearchHit[] = [
  { category: "Visitors", label: "Anil Kumar", sub: "+91 98765 43210", icon: "ri-user-3-line", to: "/admin/visitors" },
  { category: "Visitors", label: "Priya Reddy", sub: "priya.reddy@example.com", icon: "ri-user-3-line", to: "/admin/visitors" },
  { category: "Visitors", label: "Sunita Devi", sub: "Returning visitor", icon: "ri-user-3-line", to: "/admin/visitors" },
  { category: "Farmers", label: "Ramesh Naidu", sub: "Sri Lakshmi Farm", icon: "ri-user-star-line", to: "/admin/farmers" },
  { category: "Farmers", label: "Kondaiah", sub: "Konaseema Coconut Farm", icon: "ri-user-star-line", to: "/admin/farmers" },
  { category: "Farms", label: "Sri Lakshmi Farm", sub: "East Godavari", icon: "ri-plant-line", to: "/admin/farms" },
  { category: "Farms", label: "Green Valley Farm", sub: "West Godavari", icon: "ri-plant-line", to: "/admin/farms" },
  { category: "Bookings", label: "RTG-10482", sub: "Anil Kumar · Farmer for a Day", icon: "ri-calendar-check-line", to: "/admin/bookings" },
  { category: "Bookings", label: "RTG-10481", sub: "Priya Reddy · Paddy Harvest Walk", icon: "ri-calendar-check-line", to: "/admin/bookings" },
  { category: "Experiences", label: "Farmer for a Day", sub: "Farm Life · ₹999", icon: "ri-compass-3-line", to: "/admin/experiences" },
  { category: "Experiences", label: "Paddy Harvest Walk", sub: "Farm Life · ₹749", icon: "ri-compass-3-line", to: "/admin/experiences" },
  { category: "Transactions", label: "TXN-55210", sub: "₹3,996 · Paid", icon: "ri-bank-card-line", to: "/admin/payments" },
  { category: "Transactions", label: "TXN-55209", sub: "₹2,247 · Paid", icon: "ri-bank-card-line", to: "/admin/payments" },
  { category: "Content", label: "Homepage hero", sub: "Published", icon: "ri-home-4-line", to: "/admin/homepage" },
  { category: "Content", label: "Monsoon in Konaseema", sub: "Blog · Draft", icon: "ri-article-line", to: "/admin/blog" },
];

export interface QuickCreateOption {
  label: string;
  icon: string;
  to: string;
}

export const quickCreateOptions: QuickCreateOption[] = [
  { label: "Add Farmer", icon: "ri-user-add-line", to: "/admin/farmers" },
  { label: "Add Farm", icon: "ri-plant-line", to: "/admin/farms" },
  { label: "Create Experience", icon: "ri-compass-3-line", to: "/admin/experiences" },
  { label: "Create Package", icon: "ri-archive-2-line", to: "/admin/packages" },
  { label: "Create Booking", icon: "ri-calendar-check-line", to: "/admin/bookings" },
  { label: "Block Availability", icon: "ri-calendar-close-line", to: "/admin/availability" },
  { label: "Create Coupon", icon: "ri-coupon-3-line", to: "/admin/coupons" },
  { label: "Add Content", icon: "ri-add-circle-line", to: "/admin/website-pages" },
];

export interface AdminNotification {
  title: string;
  sub: string;
  icon: string;
  tone: "primary" | "accent" | "secondary";
}

export const adminNotifications: AdminNotification[] = [
  { title: "New farmer application received", sub: "Ramesh Naidu · 2 hours ago", icon: "ri-file-user-line", tone: "accent" },
  { title: "Booking #RTG-10482 confirmed", sub: "Anil Kumar · 4 hours ago", icon: "ri-calendar-check-line", tone: "primary" },
  { title: "Safety audit due for Green Valley Farm", sub: "Due in 3 days", icon: "ri-shield-flash-line", tone: "secondary" },
  { title: "₹42,500 farmer settlement ready", sub: "Sri Lakshmi Farm · awaiting approval", icon: "ri-hand-coin-line", tone: "primary" },
];